import { useId, useState, type ComponentType } from 'react';
import {
    ArrowUpCircleIcon,
    ArrowUturnLeftIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import { ConfirmModalShell } from '../approval-review/ConfirmModalShell';
import { ConfirmModalHeader } from '../approval-review/ConfirmModalHeader';
import { ConfirmModalFooter } from '../approval-review/ConfirmModalFooter';
import { MAX_HANDOFF_REASON_LENGTH } from '../../../../services/virtualAppointments';
import type { VaReasonHandoffKind } from './useVaHandoff';

const NEAR_LIMIT_RATIO = 0.9;

interface ReasonHandoffCopy {
    icon: ComponentType<{ className?: string }>;
    title: string;
    body: string;
    fieldLabel: string;
    placeholder: string;
    movedNote: (count: number) => string;
    movedNoteClassName: string;
    movedIconClassName: string;
}

function vaNoun(count: number): string {
    return count === 1 ? 'VA' : 'VAs';
}

const COPY: Record<VaReasonHandoffKind, ReasonHandoffCopy> = {
    'escalate-to-clinical': {
        icon: ArrowUpCircleIcon,
        title: 'Escalate to Clinical',
        body: 'The case moves to Clinical VA needed so Clinical can work the open VAs.',
        fieldLabel: 'Why are you escalating?',
        placeholder: 'What should Clinical look at?',
        movedNote: (count) => `The ${count} open DM ${vaNoun(count)} will move to Clinical.`,
        movedNoteClassName: 'text-amber-800 bg-amber-50 border-amber-200',
        movedIconClassName: 'text-amber-600'
    },
    'hand-back-to-dm': {
        icon: ArrowUturnLeftIcon,
        title: 'Hand back to DM',
        body: 'The case moves to DM VA needed so DM can work the open VAs.',
        fieldLabel: 'What should DM know?',
        placeholder: 'Your answer, and what DM should do next',
        movedNote: (count) => `The ${count} open Clinical ${vaNoun(count)} will move to DM.`,
        movedNoteClassName: 'text-blue-800 bg-blue-50 border-blue-200',
        movedIconClassName: 'text-blue-600'
    }
};

function counterTone(length: number): string {
    return length >= MAX_HANDOFF_REASON_LENGTH * NEAR_LIMIT_RATIO
        ? 'text-amber-600'
        : 'text-gray-400';
}

interface HandoffReasonFieldProps {
    label: string;
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
}

function HandoffReasonField({
    label,
    placeholder,
    value,
    onChange,
    disabled
}: HandoffReasonFieldProps) {
    const id = useId();
    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
                <label htmlFor={id} className="text-xs font-semibold text-gray-700">
                    {label}
                </label>
                <span className={`text-[11px] tabular-nums ${counterTone(value.length)}`}>
                    {value.length.toLocaleString()} / {MAX_HANDOFF_REASON_LENGTH.toLocaleString()}
                </span>
            </div>
            <textarea
                id={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                maxLength={MAX_HANDOFF_REASON_LENGTH}
                rows={4}
                disabled={disabled}
                required
                autoFocus
                placeholder={placeholder}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm text-gray-700 leading-relaxed resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 disabled:opacity-50"
            />
        </div>
    );
}

interface VaHandoffReasonModalProps {
    kind: VaReasonHandoffKind;
    /** Open VAs of the team being left, which the backend moves along with the case. */
    movedCount: number;
    submitting: boolean;
    error: string | null;
    onConfirm: (reason: string) => void;
    onClose: () => void;
}

// Escalating and handing back are the two halves of one Q&A, so both require the author's text.
export function VaHandoffReasonModal({
    kind,
    movedCount,
    submitting,
    error,
    onConfirm,
    onClose
}: VaHandoffReasonModalProps) {
    const [reason, setReason] = useState('');
    const copy = COPY[kind];
    const hasReason = reason.trim().length > 0;
    return (
        <ConfirmModalShell widthClassName="w-[480px]" dismissible={!submitting} onDismiss={onClose}>
            <ConfirmModalHeader
                icon={copy.icon}
                iconClassName="w-5 h-5 text-gray-700"
                title={copy.title}
                onClose={onClose}
                disabled={submitting}
            />
            <div className="px-5 py-4 flex flex-col gap-3">
                <p className="text-sm text-gray-600">{copy.body}</p>
                <HandoffReasonField
                    label={copy.fieldLabel}
                    placeholder={copy.placeholder}
                    value={reason}
                    onChange={setReason}
                    disabled={submitting}
                />
                {movedCount > 0 && (
                    <p
                        className={`flex items-start gap-2 text-xs border rounded-md px-3 py-2 ${copy.movedNoteClassName}`}
                    >
                        <InformationCircleIcon
                            className={`w-4 h-4 flex-shrink-0 ${copy.movedIconClassName}`}
                        />
                        {copy.movedNote(movedCount)}
                    </p>
                )}
                {error && (
                    <p
                        role="alert"
                        className="text-sm text-red-800 bg-red-50 border border-red-300 rounded-md px-3 py-2"
                    >
                        {error}
                    </p>
                )}
            </div>
            <ConfirmModalFooter
                onCancel={onClose}
                onConfirm={() => onConfirm(reason)}
                cancelDisabled={submitting}
                confirmDisabled={submitting || !hasReason}
                confirmLabel={submitting ? 'Working…' : copy.title}
                confirmColorClassName="bg-gray-800 text-white hover:bg-gray-900"
            />
        </ConfirmModalShell>
    );
}
